import React, { useState, useEffect } from 'react';
import { IProps, AddressInfo } from './types';
import { Field } from 'formik';
import { Postcode, AreaContent } from 'bff/models/info';
import _ from 'lodash';
import commonService from 'utils/commonService';

const pickedCities = [
  { cityCode: '100', order: 1 },
  { cityCode: '120', order: 2 },
  { cityCode: '190', order: 3 },
  { cityCode: '260', order: 4 },
  { cityCode: '170', order: 5 },
  { cityCode: '250', order: 6 }
];

const AddressComponent: React.FC<IProps> = (props) => {
  /** 通訊地址-縣市值資訊 */
  const [cityId, setCityId] = useState<string>();
  /** 通訊地址展開後的資料 */
  const [addressArray, setAddressArray] = useState<AddressInfo[]>(new Array(0));

  useEffect(() => {
    setCityId(props.cityIdVal);
  }, [props.cityIdVal]);

  // #region 通訊地址相關方法

  /**
   * @description 通訊地址縣市選單選取事件
   * @param setFieldValue 表單設值方法
   * @param areaName 區域中文名稱
   */
  const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>, setFieldValue: any, areaName?: string) => {
    const cityName = event.currentTarget.selectedOptions.item(0)?.text;

    const postCode = _.find(addressArray, (address) => {
      return address.CITY_NAME === cityName && address.AREA_NAME === areaName;
    });

    handleSetAddressVal(
      setFieldValue,
      postCode?.CITY_ID ?? event.target.value,
      postCode?.AREA_ID,
      postCode?.POST_CODE,
      !postCode
    );

    commonService.setSelectOptName(setFieldValue, props.cityIdText, cityName);
  };

  /**
   * @description 通訊地址區域選單選取事件
   * @param setFieldValue 表單設值方法
   */
  const handleCityAreaChange = (event: React.ChangeEvent<HTMLSelectElement>, setFieldValue: any) => {
    commonService.setSelectOptName(setFieldValue, props.areaIdText, event.currentTarget.selectedOptions.item(0)?.text);

    let postCode: string = '';
    const addressInfo = _.find(addressArray, (address: AddressInfo) => {
      return address.CITY_ID === cityId && address.AREA_ID === event.target.value;
    });

    if (addressInfo) {
      postCode = addressInfo.POST_CODE;
    }

    handleSetAddressVal(setFieldValue, cityId, event.target.value, postCode);
  };

  /**
   * @description 通訊地址區域郵遞區號失去焦點事件
   * @param setFieldValue 表單設值方法
   * @errorCode 郵遞區號檢核錯誤的訊息
   */
  const handlePostcodeChange = (e: React.FocusEvent<HTMLInputElement>, setFieldValue: any) => {
    /** 輸入字元為 3 字元時進行地址搜尋 */
    if (e.currentTarget.value.length === 3) {
      const postcode = _.find(addressArray, (address: any) => {
        return address.POST_CODE === e.currentTarget.value;
      });

      /** 有地址資訊 */
      if (postcode) {
        handleSetAddressVal(setFieldValue, postcode.CITY_ID, postcode.AREA_ID, postcode.POST_CODE);
        commonService.setSelectOptName(setFieldValue, props.cityIdText, postcode.CITY_NAME);
        commonService.setSelectOptName(setFieldValue, props.areaIdText, postcode.AREA_NAME);
        return;
      }
    }
    /** 設地址資訊為空 */
    handleSetAddressVal(setFieldValue, '', '', '', true);
  };

  /**
   * @description 設定通訊地址資訊
   * @param setFieldValue 表單設值方法
   * @param cityId 縣市值，預設為空
   * @param areaId 區域值，預設為空
   * @param postCode 郵遞區號值，預設為空
   * @param isReset 是否重設 CityName 與 AreaName
   */
  const handleSetAddressVal = (
    setFieldValue: any, cityId: string = '', areaId: string = '', postCode: string = '', isReset?: boolean
  ) => {
    setCityId(cityId);
    setFieldValue(props.cityIdName, cityId);
    setFieldValue(props.areaIdName, areaId);
    setFieldValue(props.postCodeName, postCode);

    if (isReset) {
      commonService.setSelectOptName(setFieldValue, props.cityIdText);
      commonService.setSelectOptName(setFieldValue, props.areaIdText);
    }

    if (props.onAddressChange) {
      const addressInfo = handleGetAddressInfo(cityId, areaId);
      if (addressInfo) {
        props.onAddressChange(addressInfo.CITY_ID, addressInfo.AREA_ID, addressInfo.POST_CODE, addressInfo.CITY_NAME, addressInfo.AREA_NAME, props.addressVal);
      }
    }
  };

  // #endregion

  // #region 建立通訊地址 縣市、區域的下拉選單

  const cityOpt: JSX.Element[] = new Array(0);
  const areaOpt: JSX.Element[] = new Array(0);
  if (props.postcodes && props.postcodes.length > 0) {
    _.forEach(props.postcodes, (postcode: Postcode, cityIndex: number) => {
      cityOpt.push(<option key={cityIndex} value={postcode.CITY_ID}>{postcode.CITY_NAME}</option>);
      // 已選取縣市，取得對應縣市的區域
      if (cityId && cityId === postcode.CITY_ID) {
        _.forEach(postcode.AREA, (area: AreaContent, areaIndex: number) => {
          areaOpt.push(<option key={areaIndex} value={area.AREA_ID}>{area.AREA_NAME}</option>);
        });
      };
      // 未選取縣市，取得所有區域資訊
      if (!cityId) {
        _.forEach(postcode.AREA, (area: AreaContent, areaIndex: number) => {
          areaOpt.push(<option key={area.AREA_ID} value={area.AREA_ID}>{area.AREA_NAME}</option>);
        });
      };
    });

    // 取得所有縣市、區域攤平的資料
    if (addressArray.length === 0) {
      const allAddress: AddressInfo[] = new Array(0);
      _.forEach(props.postcodes, (postcode: Postcode, cityIndex: number) => {
        _.forEach(postcode.AREA, (area: AreaContent, areaIndex: number) => {
          allAddress.push({
            CITY_ID: postcode.CITY_ID,
            CITY_NAME: postcode.CITY_NAME,
            AREA_ID: area.AREA_ID,
            AREA_NAME: area.AREA_NAME,
            POST_CODE: area.POST_CODE
          });
        });
      });
      setAddressArray(allAddress);
    }
  }

  // #endregion

  /**
   * @description 詳細地址更改事件
  */
  const handleDetailAddrChange = (cityId: string, areaId: string, val: string) => {
    const addressInfo = handleGetAddressInfo(cityId, areaId);

    if (addressInfo) {
      props.onAddressChange(addressInfo.CITY_ID, addressInfo.AREA_ID, addressInfo.POST_CODE, addressInfo.CITY_NAME, addressInfo.AREA_NAME, val);
    } else {
      props.onAddressChange('', '', '', '', '', val);
    }
  };

  const handleGetAddressInfo = (cityId: string, areaId: string) => {
    return _.find(addressArray, (address: AddressInfo) => {
      return address.CITY_ID === cityId && address.AREA_ID === areaId;
    });
  };

  return !props.quickPick ? (
    <>
      <Field
        as="select"
        name={props.cityIdName}
        data-fieldname={props.cityIdName}
        tabIndex={props.cityIdTabIndex}
        id={props.cityId}
        className="element__select"
        disabled={props.disabled}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          handleCityChange(e, props.setFieldValue, props.areaNameVal);
          props.handleChange(props.cityIdName)(e);
        }}
      >
        <option value="">請選擇</option>
        {cityOpt}
      </Field>
      <Field
        as="select"
        name={props.areaIdName}
        data-fieldname={props.areaIdName}
        tabIndex={props.areaIdTabIndex}
        id={props.areaId}
        className="element__select"
        disabled={props.disabled}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          handleCityAreaChange(e, props.setFieldValue);
          props.handleChange(props.areaIdName)(e);
        }}
      >
        <option value="">請選擇</option>
        {areaOpt}
      </Field>
      <Field
        type="text"
        name={props.postCodeName}
        data-fieldname={props.postCodeName}
        tabIndex={props.postCodeIdTabIndex}
        id={props.postCodeId}
        className="element__input"
        maxLength={6}
        autoComplete="off"
        disabled
        placeholder="郵遞區號"
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          props.handleBlur(e);
          handlePostcodeChange(e, props.setFieldValue);
        }}
      />
      {
        !props.hideAddress && (
          <>
            {!props.addressProtected ? (
              <Field
                type="text"
                name={props.addressName}
                data-fieldname={props.addressName}
                tabIndex={props.addressIdTabIndex}
                id={props.addressId}
                className="element__input"
                autoComplete="off"
                maxLength={200}
                placeholder="詳細地址"
                disabled={props.disabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  props.onAddressChange && handleDetailAddrChange(props.cityIdVal || '', props.areaIdVal || '', e.currentTarget.value);
                  props.handleChange(props.addressName)(e);
                }}
              />
            ) : (
              <input type="text" className="element__input" value={props.addressVal ? commonService.hiddenStringCharactersByIndexRange(props.addressVal, 4, 10) : props.addressVal} readOnly disabled />
            )}
          </>
        )
      }
    </>
  ) : (
    <>
      <div style={{ flexBasis: '100%' }}>
        <Field
          as="select"
          name={props.cityIdName}
          data-fieldname={props.cityIdName}
          tabIndex={props.cityIdTabIndex}
          id={props.cityId}
          className="element__select"
          disabled={props.disabled}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            handleCityChange(e, props.setFieldValue, props.areaNameVal);
            props.handleChange(props.cityIdName)(e);
          }}
        >
          <option value="">請選擇</option>
          {cityOpt}
        </Field>
        <Field
          as="select"
          name={props.areaIdName}
          data-fieldname={props.areaIdName}
          tabIndex={props.areaIdTabIndex}
          id={props.areaId}
          className="element__select"
          disabled={props.disabled}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            handleCityAreaChange(e, props.setFieldValue);
            props.handleChange(props.areaIdName)(e);
          }}
        >
          <option value="">請選擇</option>
          {areaOpt}
        </Field>
        <Field
          type="text"
          name={props.postCodeName}
          data-fieldname={props.postCodeName}
          tabIndex={props.postCodeIdTabIndex}
          id={props.postCodeId}
          className="element__input"
          maxLength={6}
          autoComplete="off"
          disabled
          placeholder="郵遞區號"
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            props.handleBlur(e);
            handlePostcodeChange(e, props.setFieldValue);
          }}
        />
        {
          !props.hideAddress && (
            <>
              {!props.addressProtected ? (
                <Field
                  type="text"
                  name={props.addressName}
                  data-fieldname={props.addressName}
                  tabIndex={props.addressIdTabIndex}
                  id={props.addressId}
                  className="element__input"
                  autoComplete="off"
                  maxLength={200}
                  placeholder="詳細地址"
                  disabled={props.disabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    props.onAddressChange && handleDetailAddrChange(props.cityIdVal || '', props.areaIdVal || '', e.currentTarget.value);
                    props.handleChange(props.addressName)(e);
                  }}
                />
              ) : (
                <input type="text" className="element__input" value={props.addressVal ? commonService.hiddenStringCharactersByIndexRange(props.addressVal, 4, 10) : props.addressVal} readOnly disabled />
              )}
            </>
          )
        }
      </div>
      <div style={{ flexBasis: '100%' }}>
        {props.postcodes
          .filter(postcode => pickedCities.map(city => city.cityCode).includes(postcode.CITY_ID))
          .map(postcode => ({
            CITY_ID: postcode.CITY_ID,
            CITY_NAME: postcode.CITY_NAME,
            order: pickedCities.find(cityCode => cityCode.cityCode === postcode.CITY_ID)?.order!
          }))
          .sort((a, b) => a.order - b.order)
          .map((postcode) => (
            <span
              className="element__link"
              style={{ cursor: 'pointer', marginRight: '10px' }}
              key={postcode.CITY_ID}
              onClick={() => {
                handleSetAddressVal(
                  props.setFieldValue,
                  postcode.CITY_ID,
                  undefined,
                  undefined,
                  true
                );
              }}
            >
              {postcode.CITY_NAME}
            </span>
          ))}
      </div>
    </>
  );
};

export default AddressComponent;
